import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SlimArrowUp } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { TimeLeft } from 'shared/components/ui/time-left';
import { TimeProgressChart } from 'shared/components/ui/time-progress-chart';
import { DateEntry } from 'shared/lib/recharts';
import { Paths } from 'shared/routes';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { metricUnitFormatBase } from 'shared/types/metric-template';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { formatNumberShort } from 'shared/utils/format-number-short';
import { isNumber } from 'shared/utils/is-number';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import * as Styled from './goal-insights-card.style';
import { Header } from './header';

export type GoalInsightsCardProps = {
  goal: Goal;
  entries: DateEntry[];
  completionPercentage?: number;
  unitFormat?: string;
  startValue?: number;
  currentValue?: number;
  target?: number;
  timeframeDifference?: number;
  goals: Goal[];
  lifeAreas: LifeArea[];
  startDate: Timestamp;
  endDate: Timestamp;
  onPremium: () => void;
  menuButtonRef?: React.RefObject<HTMLButtonElement>;
  onMenuButton?: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onEditGoalProgress?: (e: React.MouseEvent) => void;
  weekStartsOn: WeekDays;
  noTitle?: boolean;
  hasBigTitle?: boolean;
  isLink?: boolean;
  isLoading?: boolean;
};

export const GoalInsightsCard: React.FC<GoalInsightsCardProps> = ({
  goal,
  entries,
  completionPercentage,
  unitFormat,
  startValue,
  currentValue,
  target,
  timeframeDifference,
  goals,
  lifeAreas,
  startDate,
  endDate,
  onPremium,
  menuButtonRef,
  onMenuButton,
  onContextMenu,
  weekStartsOn,
  onEditGoalProgress,
  noTitle,
  hasBigTitle,
  isLink,
  isLoading,
}) => {
  const { t } = useTranslation();

  const lastEntryValue =
    currentValue ?? entries?.[entries.length - 1]?.value ?? startValue;
  const formattedTarget = isNumber(target)
    ? formatNumberShort(target!)
    : undefined;
  const formattedLastEntryValue = formatNumberShort(lastEntryValue ?? 0);

  const linksToDetail = !(isLoading || !isLink || goal.frozenAt);

  return (
    <Styled.Container
      as={linksToDetail ? undefined : 'span'}
      to={
        linksToDetail ? replaceUrlParams(Paths.GoalDetail, { id: goal.id }) : ''
      }
      onClick={goal.frozenAt ? onPremium : undefined}
      onContextMenu={goal.frozenAt ? undefined : onContextMenu}
    >
      <Header
        goal={goal}
        goals={goals}
        lifeAreas={lifeAreas}
        onMenuButton={onMenuButton}
        menuButtonRef={menuButtonRef}
        onEditGoalProgress={onEditGoalProgress}
        noTitle={noTitle}
        hasBigTitle={hasBigTitle}
      />

      {isLoading ? (
        <Styled.LoadingBody />
      ) : (
        <Styled.Body $isFrozen={!!goal.frozenAt}>
          <Styled.CurrentProgress>
            {!!unitFormat ? (
              <>
                <Styled.CurrentProgressValue>
                  {formattedTarget
                    ? formattedLastEntryValue
                    : unitFormat?.replace(
                        metricUnitFormatBase,
                        formattedLastEntryValue,
                      )}
                </Styled.CurrentProgressValue>
                {formattedTarget
                  ? ` / ${unitFormat?.replace(metricUnitFormatBase, formattedTarget)}`
                  : null}
              </>
            ) : (
              t('general.labels.insights-card.no-tasks.label')
            )}
          </Styled.CurrentProgress>

          <Styled.ProgressContainer>
            <Styled.Progress>
              {!goal.completedAt && !!goal.deadline && (
                <>
                  <TimeLeft date={goal.deadline} isShort />
                  <Styled.Dot />
                </>
              )}
              <Typography variant="overline">
                {completionPercentage}%
              </Typography>
            </Styled.Progress>

            {!!timeframeDifference && (
              <Styled.Difference
                $positive={
                  isNumber(startValue) &&
                  isNumber(target) &&
                  startValue! > target!
                    ? timeframeDifference < 0
                    : timeframeDifference > 0
                }
              >
                <Styled.DifferenceIconContainer $up={timeframeDifference > 0}>
                  <Icon icon={SlimArrowUp} />
                </Styled.DifferenceIconContainer>
                {Math.abs(Math.round(timeframeDifference * 10) / 10)}%
              </Styled.Difference>
            )}
          </Styled.ProgressContainer>
        </Styled.Body>
      )}

      {isLoading ? (
        <Styled.LoadingFooter />
      ) : (
        <Styled.Footer $isFrozen={!!goal.frozenAt} $isLink={!!isLink}>
          <TimeProgressChart
            data={entries}
            startDate={goal.startDate}
            endDate={goal.deadline}
            chartStartDate={startDate}
            chartEndDate={endDate}
            startValue={startValue}
            endValue={target}
            weekStartsOn={weekStartsOn}
            hideYAxis
            hasSmallHeight
          />
        </Styled.Footer>
      )}
    </Styled.Container>
  );
};
